/*
 * Tiroir de navigation mobile.
 *
 * Le panneau existe dans le DOM aux deux affichages : au-dela de 990px la
 * feuille le passe en `display: contents` et il redevient une simple rangee.
 * Le composant ne s'occupe donc que de l'etat mobile, et se desarme lui-meme
 * des que la largeur repasse au-dessus du seuil.
 *
 * Rien n'est duplique : un seul menu dans le DOM, donc un seul jeu de liens
 * annonce au lecteur d'ecran.
 */
class MenuBurger extends HTMLElement {
  static SEUIL = 990;

  connectedCallback() {
    this.bouton = this.querySelector('[data-burger]');
    this.panneau = this.querySelector('[data-panneau]');
    if (!this.bouton || !this.panneau) return;

    this.ouvert = false;
    // Ferme des le depart : le tiroir ne doit ni prendre le focus ni etre lu
    // tant qu'il est hors de l'ecran.
    if (window.innerWidth < MenuBurger.SEUIL) this.panneau.inert = true;
    this.surClic = this.surClic.bind(this);
    this.surTouche = this.surTouche.bind(this);
    this.surClicExterieur = this.surClicExterieur.bind(this);
    this.surRedimensionnement = this.surRedimensionnement.bind(this);

    this.bouton.addEventListener('click', this.surClic);
    document.addEventListener('keydown', this.surTouche);
    document.addEventListener('click', this.surClicExterieur);
    window.addEventListener('resize', this.surRedimensionnement);

    // Un lien suivi ne doit pas laisser le tiroir ouvert derriere lui : sur une
    // ancre de la meme page, la navigation ne recharge rien.
    this.panneau.addEventListener('click', (e) => {
      if (e.target.closest('a')) this.fermer();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.surTouche);
    document.removeEventListener('click', this.surClicExterieur);
    window.removeEventListener('resize', this.surRedimensionnement);
  }

  surClic(e) {
    e.stopPropagation();
    this.ouvert ? this.fermer() : this.ouvrir();
  }

  ouvrir() {
    this.ouvert = true;
    this.classList.add('entete-groupe--menu-ouvert');
    this.bouton.setAttribute('aria-expanded', 'true');
    this.panneau.inert = false;
    // Le fond ne doit pas defiler sous le tiroir. On memorise la position pour
    // la restituer : sans cela, iOS renvoie en haut de page a la fermeture.
    this.defilement = window.scrollY;
    document.body.style.top = `-${this.defilement}px`;
    document.body.classList.add('corps--fige');
    // Une frame d'attente : `visibility` vient de basculer, et un element
    // encore invisible au moment de l'appel refuserait le focus.
    requestAnimationFrame(() => {
      const premier = this.panneau.querySelector('a, button, select, input');
      if (premier) premier.focus({ preventScroll: true });
    });
  }

  fermer({ rendreFocus = true } = {}) {
    if (!this.ouvert) return;
    this.ouvert = false;
    this.classList.remove('entete-groupe--menu-ouvert');
    this.bouton.setAttribute('aria-expanded', 'false');
    if (window.innerWidth < MenuBurger.SEUIL) this.panneau.inert = true;
    document.body.classList.remove('corps--fige');
    document.body.style.top = '';
    // Le corps fige perd sa position : sans cette restitution, la fermeture
    // renvoie en haut de page.
    window.scrollTo(0, this.defilement || 0);
    if (rendreFocus) this.bouton.focus({ preventScroll: true });
  }

  surTouche(e) {
    if (!this.ouvert) return;
    if (e.key === 'Escape') { this.fermer(); return; }
    if (e.key !== 'Tab') return;

    // Piege a focus : tant que le tiroir couvre la page, la tabulation ne doit
    // pas emmener sur le contenu masque derriere.
    const cibles = [this.bouton, ...this.panneau.querySelectorAll('a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])')]
      .filter((el) => el.offsetParent !== null || el === this.bouton);
    if (!cibles.length) return;

    const premier = cibles[0];
    const dernier = cibles[cibles.length - 1];
    if (e.shiftKey && document.activeElement === premier) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && document.activeElement === dernier) {
      e.preventDefault();
      premier.focus();
    }
  }

  surClicExterieur(e) {
    if (!this.ouvert) return;
    if (!this.contains(e.target)) this.fermer({ rendreFocus: false });
  }

  surRedimensionnement() {
    // Au-dela du seuil le panneau redevient une rangee : un etat « ouvert »
    // laisserait le corps fige et le bouton annonce comme deploye.
    if (window.innerWidth >= MenuBurger.SEUIL) {
      if (this.ouvert) this.fermer({ rendreFocus: false });
      this.panneau.inert = false;
    } else if (!this.ouvert) {
      this.panneau.inert = true;
    }
  }
}

if (!customElements.get('menu-burger')) {
  customElements.define('menu-burger', MenuBurger);
}
